/** Back to front: garments first, the companion last. The same order the apps draw an avatar in. */
export const STACK_ORDER = ['legs', 'body', 'feet', 'neck', 'face', 'head', 'hand', 'companion'] as const;
export type Slot = (typeof STACK_ORDER)[number];

/** The item id worn in each slot; a slot with nothing on it is absent. */
export type Outfit = Partial<Record<Slot, string>>;

export interface Wearable {
  id: string;
  slot: Slot;
}

/** Wear the item in its slot, replacing what is there, or take it off when it is the one worn. */
export function toggleItem(outfit: Outfit, item: Wearable): Outfit {
  if (outfit[item.slot] === item.id) return removeSlot(outfit, item.slot);
  return { ...outfit, [item.slot]: item.id };
}

export function removeSlot(outfit: Outfit, slot: Slot): Outfit {
  const { [slot]: _gone, ...rest } = outfit;
  return rest;
}

/**
 * Read `<slot>=<item id>` pairs. A pair is kept only when the id is a wearable item of that slot,
 * so unknown ids, ids of another slot and unknown slots are ignored, and so is a slot repeated after its first pair.
 */
export function parseOutfit(params: URLSearchParams, wearable: readonly Wearable[]): Outfit {
  const bySlot = new Map<string, Set<string>>();
  for (const w of wearable) {
    if (!bySlot.has(w.slot)) bySlot.set(w.slot, new Set());
    bySlot.get(w.slot)!.add(w.id);
  }
  const outfit: Outfit = {};
  for (const slot of STACK_ORDER) {
    const id = params.get(slot); // the first pair for a repeated slot
    if (id && bySlot.get(slot)?.has(id)) outfit[slot] = id;
  }
  return outfit;
}

/** The query string (no leading `?`) for an outfit, slots in stack order. */
export function formatOutfit(outfit: Outfit): string {
  const params = new URLSearchParams();
  for (const slot of STACK_ORDER) if (outfit[slot]) params.set(slot, outfit[slot]!);
  return params.toString();
}

/** One random wearable item for every slot that has any. */
export function randomOutfit(wearable: readonly Wearable[], rng: () => number = Math.random): Outfit {
  const outfit: Outfit = {};
  for (const slot of STACK_ORDER) {
    const options = wearable.filter((w) => w.slot === slot);
    if (options.length) outfit[slot] = options[Math.min(options.length - 1, Math.floor(rng() * options.length))]!.id;
  }
  return outfit;
}
